import { useTranslation } from 'react-i18next';

import { useGetIssueAssessmentHistory } from '@/hooks/queries';
import { useGetIssueIdsByDashboardFilter } from '@/hooks/queries/issue-assessment/useGetIssueIdsByDashboardFilter';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useDashboardFilters } from '../../../useDashboardStore';
import { convertDateRangeValues } from '../../filterHelpers';
import LineChartWidget from '../../line-chart-widget/LineChartWidget';
import { calculateOpenIssues } from './openIssuesOverTimeUtils';

export const OpenIssuesOverTime = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgets.openIssuesOverTime',
  });
  const { loading, values } = useOpenIssuesOverTime();

  return (
    <LineChartWidget
      values={values?.map((v) => ({ x: new Date(v.x), y: v.y }))}
      isLoading={loading}
      xTitle={t('xTitle')}
      yTitle={t('yTitle')}
    />
  );
};

const useOpenIssuesOverTime = () => {
  const filters = useDashboardFilters();
  const { startDate, endDate } = convertDateRangeValues(filters.dateRange);

  // The audit history has no departments/tags relationship on the V2/GraphQL
  // leg, so resolve the department + tag filters to owning issue Ids there and
  // narrow by `ParentIssueId`. On the V3/tRPC leg the types filter server-side,
  // so the resolver is skipped and we pass the types straight through (CUS-257).
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const hasRelationshipFilter =
    filters.departments.length > 0 || filters.tags.length > 0;

  const { issueIds, loading: issueIdsLoading } =
    useGetIssueIdsByDashboardFilter({
      departmentTypeIds: filters.departments,
      tagTypeIds: filters.tags,
      isSkipped: trpcEnabled || !hasRelationshipFilter,
    });

  const { loading, data } = useGetIssueAssessmentHistory({
    queryArgs: {
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      departmentTypeIds:
        trpcEnabled && filters.departments.length ? filters.departments : null,
      tagTypeIds: trpcEnabled && filters.tags.length ? filters.tags : null,
      parentIssueIds: !trpcEnabled && hasRelationshipFilter ? issueIds : null,
    },
    // On V2 wait for the issue-id resolution before running the audit query,
    // otherwise it would flash the unfiltered series first.
    shouldSkip: !trpcEnabled && hasRelationshipFilter && issueIdsLoading,
  });

  return {
    values: data?.issue_assessment_audit
      ? calculateOpenIssues(
          'day',
          data.issue_assessment_audit,
          startDate,
          endDate
        )
      : undefined,
    loading:
      loading || (!trpcEnabled && hasRelationshipFilter && issueIdsLoading),
  };
};
